<template>
  <v-row>
    <v-col cols="9">
      <h3 class="chart-title">{{ title }}</h3>
      <div class="legend-wrapper" v-html="htmlLegend" />
      <RevenueAndProfitLineChart
        v-if="filterParams.select_filter_type"
        :chart-data="lineChartDataSets"
        @generated-legend="handleGeneratedLegend"
        :title-chart="statistic"/>
      <RevenueAndProfitLineDateChart
        v-else-if="lineChartLabels.length <= 24"
        :chart-data="lineDateChartDataSets"
        @generated-legend="handleGeneratedLegend"
        :title-chart="statistic"/>
      <div v-else class="line-chart-wrapper">
        <RevenueAndProfitLineDateChart
          class="line-chart"
          :chart-data="lineDateChartDataSets"
          @generated-legend="handleGeneratedLegend"
          :title-chart="statistic"/>
      </div>
    </v-col>
    <v-col cols="3">
      <LineChartFilter v-model="filterParams" @get-statistic="getStatistic" @get-search="submitSearch"/>
    </v-col>
    <vue-element-loading :active="isLoading" />
  </v-row>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import LineChartFilter from './RevenueAndProfitLineChart/LineChartFilter'
import RevenueAndProfitLineChart from './RevenueAndProfitLineChart/RevenueAndProfitLineChart'
import RevenueAndProfitLineDateChart from './RevenueAndProfitLineChart/RevenueAndProfitLineDateChart'

const LINE_WIDTH = 1
const LINE_TENSION = 0

const config = {
  thisYear: {
    label: '今期',
    color: '#5ed798',
    backgroundColor: 'transparent'
  },
  lastYear: {
    label: '前期',
    color: '#fd7854',
    backgroundColor: 'transparent'
  },
  twoYearsAgo: {
    label: '前々期',
    color: '#3c3bfc',
    backgroundColor: 'transparent'
  },
  period: {
    labels: [
      '2月',
      '3月',
      '4月',
      '5月',
      '6月',
      '7月',
      '8月',
      '9月',
      '10月',
      '11月',
      '12月',
      '1月'
    ]
  }
}

export default {
  name: 'RevenueAndProfitLineChartContainer',

  components: {
    LineChartFilter,
    RevenueAndProfitLineChart,
    RevenueAndProfitLineDateChart,
    VueElementLoading
  },

  props: {
    title: {
      type: String,
      default: ''
    }
  },

  computed: {
    filterDataThisYear () {
      if (this.submit) {
        return this.filterData(this.dataThisYear)
      }
    },

    filterDataLastYear () {
      if (this.submit) {
        return this.filterData(this.dataLastYear)
      }
    },

    filterDataTwoYearsAgo () {
      if (this.submit) {
        return this.filterData(this.dataTwoYearsAgo)
      }
    },

    lineChartDataSets () {
      return {
        labels: this.lineChartLabels,
        datasets: [
          {
            label: config.thisYear.label,
            year: this.filterParams.year,
            data: this.filterDataThisYear,
            backgroundColor: config.thisYear.backgroundColor,
            borderColor: config.thisYear.color,
            borderWidth: LINE_WIDTH,
            lineTension: LINE_TENSION,
            pointBorderWidth: LINE_WIDTH,
            pointBackgroundColor: config.thisYear.color
          },
          {
            label: config.lastYear.label,
            year: this.filterParams.year - 1,
            data: this.filterDataLastYear,
            backgroundColor: config.lastYear.backgroundColor,
            borderColor: config.lastYear.color,
            borderWidth: LINE_WIDTH,
            lineTension: LINE_TENSION,
            pointBorderWidth: LINE_WIDTH,
            pointBackgroundColor: config.lastYear.color
          },
          {
            label: config.twoYearsAgo.label,
            year: this.filterParams.year - 2,
            data: this.filterDataTwoYearsAgo,
            backgroundColor: config.twoYearsAgo.backgroundColor,
            borderColor: config.twoYearsAgo.color,
            borderWidth: LINE_WIDTH,
            lineTension: LINE_TENSION,
            pointBorderWidth: LINE_WIDTH,
            pointBackgroundColor: config.twoYearsAgo.color
          }
        ]
      }
    },
    lineDateChartDataSets () {
      return {
        labels: this.lineChartLabels,
        datasets: [
          {
            label: `${this.filterParams.date_from || 'yyyy/mm/dd'} - ${this.filterParams.date_to || 'yyyy/mm/dd'}`,
            data: this.filterDataThisYear,
            backgroundColor: config.thisYear.backgroundColor,
            borderColor: config.thisYear.color,
            borderWidth: LINE_WIDTH,
            lineTension: LINE_TENSION,
            pointBorderWidth: LINE_WIDTH,
            pointBackgroundColor: config.thisYear.color
          }
        ]
      }
    }
  },

  data () {
    return {
      isLoading: false,
      htmlLegend: '',
      statistic: '',
      filterParams: {},
      lineChartLabels: config.period.labels,
      dataThisYear: new Array(12).fill(0),
      dataLastYear: new Array(12).fill(0),
      dataTwoYearsAgo: new Array(12).fill(0),
      submit: false
    }
  },
  methods: {
    handleGeneratedLegend (html) {
      this.htmlLegend = html
    },

    getStatistic (statistic) {
      this.statistic = statistic
    },

    submitSearch (search) {
      if (search) {
        this.submit = search
        this.fetchLineChartData()
      }
    },

    filterData (listData) {
      switch (this.statistic) {
        case '売上額':
          if (listData) return listData.map((item) => item.revenue)
          break
        case '粗利額':
          if (listData) return listData.map((item) => item.gross_profit)
          break
        case '出荷数':
          if (listData) return listData.map((item) => item.quantity)
          break
      }
    },

    async fetchLineChartData () {
      try {
        this.isLoading = true
        const response = await this.$http.post(
          this.$api_url.line_chart,
          this.filterParams
        )
        const { status, data: { data } } = response

        if (!status) throw new Error('Something went wrong.')

        if (!this.filterParams.select_filter_type) {
          this.lineChartLabels = data[0].data.map(item => item.label)
          const convertDetailThisYear = this.$util.convertArrayOfObjectToObject(data[0].data, 'month')
          const keysDetailThisYear = Object.keys(convertDetailThisYear)
          this.dataThisYear = this.lineChartLabels.map((_, index) => convertDetailThisYear[keysDetailThisYear[index]] || 0)
        } else {
          this.lineChartLabels = config.period.labels
          const convertDetail = {
            thisYear: this.$util.convertArrayOfObjectToObject(data[0].data, 'month'),
            lastYear: this.$util.convertArrayOfObjectToObject(data[1].data, 'month'),
            twoYearsAgo: this.$util.convertArrayOfObjectToObject(data[2].data, 'month')
          }
          const keysDetail = {
            thisYear: Object.keys(convertDetail.thisYear),
            lastYear: Object.keys(convertDetail.lastYear),
            twoYearsAgo: Object.keys(convertDetail.twoYearsAgo)
          }
          this.dataThisYear = this.lineChartLabels.map((_, index) => convertDetail.thisYear[keysDetail.thisYear[index]] || 0)
          this.dataLastYear = this.lineChartLabels.map((_, index) => convertDetail.lastYear[keysDetail.lastYear[index]] || 0)
          this.dataTwoYearsAgo = this.lineChartLabels.map((_, index) => convertDetail.twoYearsAgo[keysDetail.twoYearsAgo[index]] || 0)
        }
      } catch (e) {
        this.lineChartLabels = config.period.labels
        this.dataThisYear = new Array(12).fill(0)
        this.dataLastYear = new Array(12).fill(0)
        this.dataTwoYearsAgo = new Array(12).fill(0)
        this.$toastr.error(
          'エラーのため失敗しました。ご確認後、再試行してください。',
          'エラー'
        )
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style>
.legend-item__line {
  position: relative;
  margin-right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.legend-item__line::before {
  content: "";
  height: 2px;
  background-color: inherit;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 30px;
  transform: translate(-50%, -50%);
}

.legend-item__label {
  margin-left: 10px;
}

.line-chart-wrapper {
  overflow-x: scroll;
}

.line-chart {
  width: 100vw;
}
</style>
